<div class="p-2 bg-gray-50 min-h-screen">
  <!-- PAGE HEADER -->
  <div class="flex justify-between items-center mb-4">
    <h3 class="text-lg font-semibold text-gray-800">{{ 'DISB_NOTE.TITLE' | translate }}</h3>

    <button
      type="button"
      class="text-sm font-medium text-gray-600 hover:text-indigo-600 flex items-center gap-1"
      (click)="goBack()"
    >
      ‹ {{ 'COMMON.BACK' | translate }}
    </button>
  </div>

  <!-- STATE -->
  @if (noteLoading) {
    <div class="text-sm text-gray-500 mb-2">{{ 'DISB_NOTE.LOADING_NOTE' | translate }}</div>
  }

  @if (noteError) {
    <div class="text-sm text-red-600 mb-2">{{ noteError | translate }}</div>
  }

  @if (note && !noteLoading) {
    <div class="bg-white rounded-2xl p-5 mb-4">
      <!-- READ-ONLY INFO GRID -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        @for (field of headerFields; track field.label) {
          <div
            class="rounded-2xl px-4 py-3"
            [class.lg:col-span-2]="field.wide"
            [ngClass]="field.pending ? 'border border-dashed border-gray-300' : 'bg-gray-100'"
          >
            <div class="text-xs text-gray-500">{{ field.label | translate }}</div>

            <div
              class="text-sm truncate"
              [ngClass]="field.pending ? 'text-gray-400' : 'font-medium text-gray-800'"
              [title]="field.pending ? (field.value | translate) : field.value"
            >
              {{ field.pending ? (field.value | translate) : field.value }}
            </div>
          </div>
        }
      </div>
    </div>

    <!-- EXPOSURE CALCULATION STRIP -->
    <div class="bg-white rounded-2xl p-5 mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <div>
          <div class="text-xs text-gray-500">{{ 'DISB_NOTE.APPROVED_EXPOSURE' | translate }}</div>

          <div class="text-base font-semibold" [ngClass]="hasApprovedExposure ? 'text-gray-800' : 'text-gray-400'">
            {{ hasApprovedExposure ? (approvedExposure | number: '1.0-0') : ('DISB_NOTE.NOT_SET' | translate) }}
          </div>
        </div>

        <div>
          <div class="text-xs text-gray-500">{{ 'DISB_NOTE.CURRENT_OUTSTANDING_EXPOSURE' | translate }}</div>

          <div class="text-base font-semibold text-gray-800">
            {{ currentOutstanding | number: '1.0-0' }}
          </div>
        </div>

        <div>
          <label class="text-xs text-gray-500" for="amountRecommended">
            {{ 'DISB_NOTE.AMOUNT_RECOMMENDED' | translate }} <span class="text-red-500">*</span>
          </label>

          <input
            id="amountRecommended"
            type="text"
            appAmountInput
            [(ngModel)]="amountRecommended"
            [disabled]="isViewMode"
            class="w-full h-11 px-3 border rounded-xl bg-white text-sm text-gray-800 outline-none transition-all focus:ring-2"
            [ngClass]="
              exceedsApprovedExposure
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-gray-200 focus:border-[#1E2A78] focus:ring-[#1E2A78]/10'
            "
            placeholder="0"
          />
        </div>

        <div>
          <div class="text-xs text-gray-500">{{ 'DISB_NOTE.EXPOSURE_POST_DISBURSEMENT' | translate }}</div>

          <div
            class="text-base font-semibold"
            [ngClass]="exceedsApprovedExposure ? 'text-red-600' : 'text-[#13258f]'"
          >
            {{ exposurePostDisbursement | number: '1.0-0' }}
          </div>
        </div>
      </div>

      <!-- AMOUNT IN WORDS -->
      <div class="mt-4">
        <div class="text-xs text-gray-500">{{ 'DISB_NOTE.AMOUNT_IN_WORDS' | translate }}</div>

        <div class="text-sm text-gray-700 italic">
          {{ amountInWordsText || '—' }}
        </div>
      </div>

      <!-- HEADROOM -->
      <div class="mt-4">
        @if (hasApprovedExposure) {
          <div class="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all"
              [ngClass]="exceedsApprovedExposure ? 'bg-red-500' : 'bg-indigo-600'"
              [style.width.%]="utilisationPercent"
            ></div>
          </div>

          <div
            class="mt-2 text-xs"
            [ngClass]="exceedsApprovedExposure ? 'text-red-600' : 'text-gray-500'"
          >
            @if (exceedsApprovedExposure) {
              {{ 'DISB_NOTE.EXCEEDS_APPROVED_BY' | translate: { amount: (-headroom | number: '1.0-0') } }}
            } @else {
              {{ 'DISB_NOTE.UTILISATION_REMAINING' | translate: { percent: (utilisationPercent | number: '1.0-1'), remaining: (headroom | number: '1.0-0') } }}
            }
          </div>
        } @else {
          <div class="text-xs text-gray-400">
            {{ 'DISB_NOTE.APPROVED_EXPOSURE_NOT_SET' | translate }}
          </div>
        }
      </div>

      <!-- SAVE RECOMMENDED AMOUNT -->
      @if (!isViewMode) {
      <div class="mt-4 flex justify-end">
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-[#1E2A78] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#26358f] disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="savingAmount || !canSubmit"
          (click)="saveRecommendedAmount()"
        >
          {{ (savingAmount ? 'DISB_NOTE.SAVING' : 'COMMON.SAVE') | translate }}
        </button>
      </div>
      }
    </div>

    <!-- TABS -->
    <div class="bg-white rounded-2xl p-5">
      <div class="mb-4 flex items-center justify-between gap-6 border-b border-gray-100">
        <div class="flex gap-6">
          <button
            (click)="setTab('note')"
            class="pb-2 text-sm font-medium"
            [ngClass]="
              activeTab === 'note'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-indigo-600'
            "
          >
            {{ 'DISB_NOTE.TITLE' | translate }}
          </button>

          <button
            (click)="setTab('covenants')"
            class="pb-2 text-sm font-medium"
            [ngClass]="
              activeTab === 'covenants'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-indigo-600'
            "
          >
            {{ 'DISB_NOTE.COVENANTS' | translate }}
          </button>

          <button
            (click)="setTab('financials')"
            class="pb-2 text-sm font-medium"
            [ngClass]="
              activeTab === 'financials'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-indigo-600'
            "
          >
            {{ 'DISB_NOTE.LATEST_FINANCIALS' | translate }}
          </button>

          @if (showCadTab) {
            <button
              (click)="setTab('cad')"
              class="pb-2 text-sm font-medium"
              [ngClass]="
                activeTab === 'cad'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-500 hover:text-indigo-600'
              "
            >
              {{ 'DISB_NOTE.CAD' | translate }}
            </button>
          }

          <button
            (click)="setTab('guarantorShare')"
            class="pb-2 text-sm font-medium"
            [ngClass]="
              activeTab === 'guarantorShare'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-indigo-600'
            "
          >
            {{ 'DISB_NOTE.GUARANTOR_SHARE' | translate }}
          </button>

          <button
            (click)="setTab('attachments')"
            class="pb-2 text-sm font-medium"
            [ngClass]="
              activeTab === 'attachments'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-500 hover:text-indigo-600'
            "
          >
            {{ 'DISB_NOTE.ATTACHMENTS' | translate }}
          </button>
        </div>

        <button
          type="button"
          class="mb-2 inline-flex items-center gap-2 rounded-lg bg-[#1E2A78] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#26358f] disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="downloading"
          (click)="downloadReport()"
        >
          {{ (downloading ? 'DISB_NOTE.PREPARING' : 'DISB_NOTE.DOWNLOAD_REPORT') | translate }}
        </button>
      </div>

      <!-- TAB: DISBURSEMENT NOTE -->
      @if (activeTab === 'note') {
        <div class="space-y-4">
          <div>
            <label class="text-xs font-medium text-gray-500" for="comments">
              {{ 'DISB_NOTE.COMMENTS_LABEL' | translate }}
            </label>

            <textarea
              id="comments"
              rows="6"
              [(ngModel)]="comments"
              [disabled]="isViewMode"
              class="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl bg-white text-sm text-gray-800 outline-none transition-all focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
              [placeholder]="'DISB_NOTE.COMMENTS_PLACEHOLDER' | translate"
            ></textarea>
          </div>

          <div class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
            {{ 'DISB_NOTE.SIGNATURES_NOTE' | translate }}
          </div>
        </div>
      }

      <!-- TAB: COVENANTS -->
      <!--
        Instantiated on first visit and then kept alive but hidden: the grid holds
        unsaved edits, so destroying it on every tab switch would discard them.
      -->
      @if (covenantsVisited) {
        <div [class.hidden]="activeTab !== 'covenants'">
          <app-covenant-compliance
          #covenantGrid
          [crdtPrpslSeq]="crdtPrpslSeq"
          [readOnly]="!canEditCovenantComment"
          [showComment]="showCovenantComment"
          [showSaveButton]="false"
        ></app-covenant-compliance>
        </div>
      }

      <!-- TAB: CAD (same grid, CAD questionnaire; kept alive like covenants) -->
      @if (cadVisited) {
        <div [class.hidden]="activeTab !== 'cad'">
          @if (cadLoading) {
            <div class="text-sm text-gray-500">{{ 'DISB_NOTE.LOADING_CAD' | translate }}</div>
          }

          @if (cadError && !cadLoading) {
            <div class="text-sm text-red-600">{{ cadError | translate }}</div>
          }

          @if (cadQstnrSeq > 0) {
            <app-covenant-compliance
              #cadGrid
              mode="cad"
              [prpsdDsbmntSeq]="prpsdDsbmntSeq"
              [qstnrSeq]="cadQstnrSeq"
              [crdtPrpslSeq]="crdtPrpslSeq"
              [showSaveButton]="false"
              [readOnly]="!canEditCad"
              emptyMessage="DISB_NOTE.NO_CAD_CONFIGURED"
            ></app-covenant-compliance>
          }
        </div>
      }

      <!-- TAB: GUARANTOR SHARE (kept alive like CAD) -->
      @if (guarantorShareVisited) {
        <div [class.hidden]="activeTab !== 'guarantorShare'">
          <app-guarantor-share
            [crdtPrpslSeq]="crdtPrpslSeq"
            [prpsdDsbmntSeq]="prpsdDsbmntSeq"
            [readOnly]="isViewMode"
          ></app-guarantor-share>
        </div>
      }

      <!-- TAB: ATTACHMENTS (shared document component, entity type 14) -->
      @if (activeTab === 'attachments' && prpsdDsbmntSeq > 0) {
        <!-- inPopup: no Back button / View History link — this page has its own navigation. -->
        <app-document
          class="disb-attachments"
          [moduleSeq]="ATTACHMENTS_MODULE_SEQ"
          [entitySeq]="prpsdDsbmntSeq"
          [accept]="ATTACHMENTS_ACCEPT"
          [allowedExtensions]="['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'png', 'jpg', 'jpeg']"
          [inPopup]="true"
          [showDownloadAction]="false"
          [showHistoryPopupAction]="true"
          [useFaDeleteIcon]="true"
          [viewOnly]="isViewMode"
        ></app-document>
      }

      <!-- TAB: LATEST FINANCIALS -->
      @if (activeTab === 'financials') {
        <div>
          @if (financialsLoading) {
            <div class="text-sm text-gray-500">{{ 'DISB_NOTE.LOADING_FINANCIALS' | translate }}</div>
          }

          @if (financialsError) {
            <div class="text-sm text-red-600">{{ financialsError | translate }}</div>
          }

          @if (!financialsLoading && !financialsError) {
            @if (financialData.length === 0) {
              <div class="text-sm text-gray-400">
                {{ 'DISB_NOTE.NO_FINANCIAL_ANALYSIS' | translate }}
              </div>
            } @else {
              <app-table
                [columns]="financialColumns"
                [data]="financialData"
                [enablePagination]="true"
                [pageSize]="25"
                searchPlaceholder="DISB_NOTE.SEARCH_INDICATOR"
              >
              </app-table>
            }
          }
        </div>
      }
    </div>

    <!-- The blocking value lives on the first tab, so say why it is blocked. -->
    @if (isLastTab && !canSubmit && !isViewMode) {
      <p class="text-center text-sm text-amber-600 pt-4">
        @if (exceedsApprovedExposure) {
          {{ 'DISB_NOTE.ERR_EXCEEDS_CORRECT_ON_TAB' | translate }}
        } @else {
          {{ 'DISB_NOTE.ERR_ENTER_AMOUNT_ON_TAB' | translate }}
        }
      </p>
    }

    <!-- FOOTER ACTIONS -->
    <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-center gap-3 py-6">
      <!-- BACK / CANCEL -->
      <button
        type="button"
        class="w-full sm:w-auto min-w-[140px] px-6 py-2.5 rounded-full border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
        [disabled]="submitting"
        (click)="isFirstTab ? goBack() : goPrevious()"
      >
        {{ (isFirstTab ? 'COMMON.CANCEL' : 'COMMON.BACK') | translate | uppercase }}
      </button>

      <!-- SAVE (only where the tab has savable data) -->
      @if (canSaveActiveTab) {
        <button
          type="button"
          class="w-full sm:w-auto min-w-[140px] px-6 py-2.5 rounded-full bg-[#1E2A78] text-white font-medium transition hover:bg-[#26358f]"
          (click)="saveActiveTab()"
        >
          {{ 'COMMON.SAVE' | translate | uppercase }}
        </button>
      }

      <!-- CONTINUE until the last tab, then the final submission -->
      @if (!isLastTab) {
        <button
          type="button"
          class="w-full sm:w-auto min-w-[140px] px-6 py-2.5 rounded-full bg-indigo-600 text-white font-medium hover:bg-indigo-700"
          (click)="goNext()"
        >
          {{ 'COMMON.CONTINUE' | translate | uppercase }}
        </button>
      } @else if (!isViewMode) {
        <button
          type="button"
          class="w-full sm:w-auto min-w-[220px] px-6 py-2.5 rounded-full bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="!canSubmit || submitting"
          (click)="askToSubmit()"
        >
          {{ (submitting ? 'DISB_NOTE.SUBMITTING' : 'DISB_NOTE.SUBMIT_NOTE_BUTTON') | translate }}
        </button>
      }
    </div>
  }
</div>

<!-- SUBMIT CONFIRMATION -->
@if (showSubmitConfirm) {
  <app-popup
    [(show)]="showSubmitConfirm"
    title="DISB_NOTE.SUBMIT_NOTE_TITLE"
    type="confirm"
    [message]="'DISB_NOTE.SUBMIT_CONFIRM_MESSAGE' | translate: { amount: (amountRecommended | number: '1.0-0') }"
    confirmText="COMMON.SUBMIT"
    cancelText="COMMON.CANCEL"
    (confirmed)="submitNote()"
  >
  </app-popup>
}
